import React, { useState } from 'react';
import type { RttsGateFields } from '../types';
import { formatGameContext } from '../lib/formatGameContext';

export type BrandedReelFallbackProps = {
  gates: Pick<
    RttsGateFields,
    'matchup' | 'season_label' | 'week_label' | 'broadcaster_handle'
  >;
  className?: string;
  /** Optional test id override. */
  testId?: string;
};

/**
 * Concept D branded title-card fallback when poster_url is missing or fails to load.
 * Never renders a broken <img> or bare black frame.
 */
const BrandedReelFallback: React.FC<BrandedReelFallbackProps> = ({
  gates,
  className,
  testId = 'rtts-branded-fallback',
}) => {
  const matchup = (gates.matchup || 'League game').trim().toUpperCase();
  const context = formatGameContext(gates);

  return (
    <div
      className={[
        'relative flex h-full w-full flex-col justify-end overflow-hidden bg-gradient-to-br from-secondary-950 via-secondary-900 to-[#2a1848]',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid={testId}
    >
      <div
        className="pointer-events-none absolute left-0 top-0 h-full w-1.5 bg-[#8B5CF6]"
        aria-hidden
      />
      <div className="absolute right-3 top-3 rounded border border-[#8B5CF6]/40 bg-black/40 px-2 py-1 text-[10px] font-semibold tracking-wide text-[#c4b5fd]">
        RTTS
      </div>
      <div className="space-y-1 p-4 pl-5">
        <p className="text-sm font-semibold uppercase tracking-wide text-white line-clamp-2">
          {matchup}
        </p>
        {context ? (
          <p className="text-xs text-secondary-300 line-clamp-1">{context}</p>
        ) : null}
        <p className="text-[10px] uppercase tracking-wider text-secondary-500">
          Scoutlytics Studio
        </p>
      </div>
    </div>
  );
};

/**
 * Poster image that falls back to {@link BrandedReelFallback} on missing URL or load error.
 */
export function ReelPosterThumb({
  posterUrl,
  gates,
}: {
  posterUrl: string | null;
  gates: BrandedReelFallbackProps['gates'];
}): React.ReactElement {
  const [failed, setFailed] = useState(false);
  const showImage = Boolean(posterUrl) && !failed;

  if (!showImage) {
    return <BrandedReelFallback gates={gates} />;
  }

  return (
    <img
      src={posterUrl!}
      alt=""
      className="h-full w-full object-cover"
      data-testid="rtts-inline-poster"
      onError={() => setFailed(true)}
    />
  );
}

export default BrandedReelFallback;
